"use client"
import { useParams } from "next/navigation"
import { getLanguageFromLocale } from "@/lib/i18n/utils"
import { getCurrentDevice } from "@/lib/utils.client"
import type { MediaData } from "./schema"
import type { CMSImageData, CMSLinkData, Translatable } from "./types"
import { getTranslatedText } from "./utils.server"

/**
 * 获取翻译文本
 * @param translatable 翻译文本
 * @param fallbackLocale 备用语言
 * @returns 翻译文本
 * 单个文本字段的翻译
 * 简单的标题、按钮文本、提示信息等
 * 不需要处理嵌套结构的场景
 * 只需要获取当前语言的文本内容
 */
export function useTranslatedText(
	translatable?: Translatable | string, // 翻译文本
	fallbackLocale: string = "en", // 备用语言
): string {
	const { locale } = useParams()
	const lang = getLanguageFromLocale(locale as string)
	const currentLocale = typeof lang === "string" ? lang : fallbackLocale

	return getTranslatedText(currentLocale, translatable, fallbackLocale)
}

/**
 * 获取集合URL
 *
 */
export function getCollectionUrl(collectionId?: string): string | undefined {
	if (!collectionId) return undefined
	return `/collections/${collectionId}`
}

/**
 * 确保内部URL以"/"开头
 * @param url 内部URL
 * @returns 规范化的内部URL
 */
export function normalizeInternalUrl(url?: string): string {
	if (!url) return "/"
	return url.startsWith("/") ? url : `/${url}`
}

/**
 * 验证外部URL是否有效
 * @param url 外部URL
 * @returns 如果URL有效则返回true，否则返回false
 */
export function validateExternalUrl(url?: string): boolean {
	if (!url) return false
	try {
		new URL(url)
		return true
	} catch (_e) {
		console.warn("Invalid external URL:", url)
		return false
	}
}

/**
 * 根据链接类型和数据获取URL
 * @param link 链接数据对象
 * @returns 链接URL或undefined
 */
export function getLinkUrl(link?: CMSLinkData): string | undefined {
	if (!link) return undefined

	switch (link.type) {
		case "internal":
			return link.internalUrl
				? normalizeInternalUrl(link.internalUrl)
				: undefined
		case "collection":
			return link.collectionId ? getCollectionUrl(link.collectionId) : undefined
		case "external":
			return validateExternalUrl(link.externalUrl)
				? link.externalUrl
				: undefined
		case "none":
		default:
			return undefined
	}
}

export function getMediaUrl(media: MediaData | string | null): string {
	if (!media) return ""
	if (typeof media === "string") {
		return ""
	}
	if (media.type === "image") {
		return media.image?.pcUrl || media.image?.mobUrl || ""
	}
	if (media.type === "video") {
		return media.video?.pcUrl || media.video?.mobUrl || ""
	}
	return ""
}

export function getImageUrlByDevice(
	image?: CMSImageData | string | null,
): string {
	if (!image) return ""
	if (typeof image === "string") {
		return ""
	}

	const device = getCurrentDevice()
	if (device === "mob") {
		return image.mobUrl || ""
	}
	if (device === "pc") {
		return image.pcUrl || ""
	}

	return ""
}
